/**
 * Beko Token (Beko X30TR / 300TR) Fiscal Device Integration
 * Token X Connect Yazarkasa Entegrasyonu
 * 
 * Beko X30TR: Basit yazarkasa (sendBasket ile tek işlem)
 * Beko 300TR: Gelişmiş yazarkasa (sendPayment, cancelReceipt desteği, parçalı ödeme)
 */

import {
  IFiscalDevice,
  FiscalProductItem,
  FiscalOperationResult,
  FiscalApiResponse,
  MultiplePosPayment,
  PaymentStepCallback,
} from './types';

const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';
let currentLocalServiceUrl = DEFAULT_LOCAL_SERVICE_URL;

export const setLocalServiceUrl = (url: string) => {
  if (url && url !== currentLocalServiceUrl) {
    console.log('[Beko Token] 🔌 Yerel servis URL güncellendi:', url);
    currentLocalServiceUrl = url;
  }
};

const getLocalServiceApiBaseUrl = () => `${currentLocalServiceUrl}/api/yazarkasa`;

const fetchWithFallback = async (url: string, options: RequestInit): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    return response;
  } catch (primaryError) {
    if (currentLocalServiceUrl !== DEFAULT_LOCAL_SERVICE_URL) {
      const fallbackUrl = url.replace(currentLocalServiceUrl, DEFAULT_LOCAL_SERVICE_URL);
      console.warn(`[LocalService] Birincil URL başarısız (${currentLocalServiceUrl}), fallback deneniyor: ${DEFAULT_LOCAL_SERVICE_URL}`);
      return await fetch(fallbackUrl, options);
    }
    throw primaryError;
  }
};

interface TokenSection {
  sectionNo: number;
  name: string;
  taxPercent: number;
  limit: number;
  price: number;
  type: number;
}

interface TokenPLU {
  pluNo: number;
  name: string;
  price: number;
  sectionNo: number;
  taxPercent: number;
  barcode: string;
  type: number;
  unit: string;
  vatID: number;
  limit: number;
}

interface TokenFiscalInfo {
  sections: TokenSection[];
  plus: TokenPLU[];
  sectionCount: number;
  pluCount: number;
  receiptLimit: number | null;
  businessMode: number;
}

interface BekoActualPaymentItem {
  amount: number;
  type: number;
  description?: string;
  BatchNo?: number;
  TxnNo?: number;
  operatorId?: number;
  status?: number;
  currencyId?: number;
}

const BEKO_PAYMENT_TYPE_NAMES: { [key: number]: string } = {
  1: 'Nakit',
  2: 'Çek',
  3: 'Kredi Kartı',
  7: 'Yemek Kartı',
  8: 'Ödemesiz',
  9: 'İkram',
  11: 'Puan',
  12: 'EPOS',
  13: 'Mobil Ödeme',
  14: 'E-Para',
  15: 'Bağış',
  16: 'Bond',
  17: 'Açık Hesap',
  18: 'Para Transferi',
  19: 'Ulaşım Kartı',
  20: 'Hediye Kartı',
};

/**
 * Cihazdan dönen receiptData'yı parse ederek gerçek ödeme bilgilerini çıkarır.
 * Kasiyerin cihaz üzerinde ödeme türünü değiştirmesini algılamak için kullanılır.
 */
const parseReceiptPaymentItems = (
  receiptDataStr: string | undefined,
  sentPaymentTypes: number[]
): { actualPaymentItems: BekoActualPaymentItem[]; paymentTypeChanged: boolean } => {
  if (!receiptDataStr) {
    return { actualPaymentItems: [], paymentTypeChanged: false };
  }

  try {
    const receiptData = typeof receiptDataStr === 'string' ? JSON.parse(receiptDataStr) : receiptDataStr;
    const actualPaymentItems: BekoActualPaymentItem[] = receiptData.paymentItems || [];

    if (actualPaymentItems.length === 0) {
      return { actualPaymentItems: [], paymentTypeChanged: false };
    }

    const actualTypes = actualPaymentItems.map(p => p.type).sort();
    const sentTypes = [...sentPaymentTypes].sort();

    let paymentTypeChanged = false;

    if (actualTypes.length !== sentTypes.length) {
      paymentTypeChanged = true;
    } else {
      for (let i = 0; i < actualTypes.length; i++) {
        if (actualTypes[i] !== sentTypes[i]) {
          paymentTypeChanged = true;
          break;
        }
      }
    }

    if (paymentTypeChanged) {
      const sentNames = sentTypes.map(t => BEKO_PAYMENT_TYPE_NAMES[t] || `Tip ${t}`).join(', ');
      const actualNames = actualTypes.map(t => BEKO_PAYMENT_TYPE_NAMES[t] || `Tip ${t}`).join(', ');
      console.log(`[Beko Token] ⚠️ ÖDEME TÜRÜ DEĞİŞTİ! Gönderilen: [${sentNames}] → Cihazdan dönen: [${actualNames}]`);
      
      actualPaymentItems.forEach((p, i) => {
        const typeName = BEKO_PAYMENT_TYPE_NAMES[p.type] || `Tip ${p.type}`;
        console.log(`[Beko Token]   Ödeme ${i + 1}: ${typeName} - ${(p.amount / 100).toFixed(2)} TL`);
      });
    }

    return { actualPaymentItems, paymentTypeChanged };
  } catch (error) {
    console.error('[Beko Token] receiptData parse hatası:', error);
    return { actualPaymentItems: [], paymentTypeChanged: false };
  }
};

let fiscalInfoCache: TokenFiscalInfo | null = null;
let isInitialized = false;
let targetDeviceIndex: number = -1;

/**
 * Kullanıcının seçtiği cihaz türüne göre hedef device index'i ayarlar.
 * beko_x30tr → 0, beko_300tr → 1
 */
export const setTargetDeviceIndex = (selectedDevice: string) => {
  if (selectedDevice === 'beko_x30tr') {
    targetDeviceIndex = 0;
  } else if (selectedDevice === 'beko_300tr') {
    targetDeviceIndex = 1;
  } else {
    targetDeviceIndex = -1;
  }
  console.log(`[Beko Token] Hedef cihaz ayarlandı: ${selectedDevice} → deviceIndex=${targetDeviceIndex}`);
};

const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Timeout sonrası cihazdan geç gelen yanıtı kontrol eder.
 * serialInCallback arka planda hala dinlediği için, timeout sonrası da
 * başarılı/başarısız yanıt gelebilir. Bu fonksiyon o yanıtı yakalar.
 */
export const checkBekoLastReceiptStatus = async (): Promise<{
  hasResult: boolean;
  receiptStatus: number;
  status: string;
  receiptData?: string;
} | null> => {
  try {
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/token-check-last-receipt`, { method: 'GET' });
    if (!response.ok) return null;
    const data = await response.json();
    if (data.success && data.data) {
      return data.data;
    }
    return null;
  } catch {
    return null;
  }
};

const callTokenAPI = async (
  endpoint: string,
  body: any,
  method: 'GET' | 'POST' = 'POST',
  showAlertModal?: (title: string, message: string) => void
): Promise<FiscalApiResponse> => {
  // ⏱️ TIMING DEBUG: 50/100/150 kalemli sepetlerde nerede tıkandığını ölçmek için
  // detaylı süre logları. Production'da gürültü oluşturabilir; sorun çözüldükten
  // sonra `__BEKO_TIMING_LOG` flag'i ile kapatılabilir hale getirilebilir.
  const __t0 = performance.now();
  let __tStringify: number | null = null;
  let __tFetchStart: number | null = null;
  let __tFetchEnd: number | null = null;
  let __tParsed: number | null = null;
  let __payloadSizeKB: number | null = null;
  let __itemCount: number | null = null;
  try {
    // Sepet kalem sayısını öğren (sadece send-basket için)
    try {
      const items = body?.basket?.items;
      if (Array.isArray(items)) __itemCount = items.length;
    } catch { /* ignore */ }

    console.log(`[Beko Token API] ${method} ${endpoint} başlatıldı`, body);

    const fetchOptions: RequestInit = {
      method: method,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    if (method === 'POST' && body) {
      const __ts0 = performance.now();
      const json = JSON.stringify(body);
      fetchOptions.body = json;
      __tStringify = performance.now() - __ts0;
      __payloadSizeKB = Math.round((new Blob([json]).size / 1024) * 10) / 10;
      console.log(
        `[Beko Token TIMING] ⏱️ JSON.stringify: ${__tStringify.toFixed(1)}ms | ` +
        `payload=${__payloadSizeKB}KB | items=${__itemCount ?? '-'}`
      );
    }

    __tFetchStart = performance.now();
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/${endpoint}`, fetchOptions);
    __tFetchEnd = performance.now();
    console.log(
      `[Beko Token TIMING] ⏱️ fetch (network + local-service + C# round-trip): ` +
      `${(__tFetchEnd - __tFetchStart).toFixed(0)}ms | endpoint=${endpoint}`
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP error! status: ${response.status}`,
      }));
      console.error(`[Beko Token API] ${endpoint} hatası:`, errorData);
      return { success: false, data: errorData, rawResponse: response };
    }

    const __tp0 = performance.now();
    const data = await response.json();
    __tParsed = performance.now() - __tp0;

    if (data.success === false) {
      console.error(`[Beko Token API] ${endpoint} işlem hatası:`, data);
      return { success: false, data, rawResponse: response };
    }

    const __tTotal = performance.now() - __t0;
    console.log(
      `[Beko Token TIMING] ✅ ${endpoint} TOPLAM: ${__tTotal.toFixed(0)}ms ` +
      `(stringify=${__tStringify?.toFixed(1) ?? '-'}ms, ` +
      `fetch=${(__tFetchEnd! - __tFetchStart!).toFixed(0)}ms, ` +
      `response.json=${__tParsed.toFixed(1)}ms, items=${__itemCount ?? '-'}, payload=${__payloadSizeKB ?? '-'}KB)`
    );
    console.log(`[Beko Token API] ${endpoint} başarılı`, data);
    return { success: true, data, rawResponse: response };

  } catch (error: any) {
    const __tErr = performance.now() - __t0;
    console.error(
      `[Beko Token TIMING] ❌ ${endpoint} HATA (toplam ${__tErr.toFixed(0)}ms):`, error
    );
    console.error(`[Beko Token API] ${endpoint} genel hatası:`, error);
    return { success: false, data: { message: error.message }, rawResponse: null };
  }
};

const initializeBekoTokenDevice = async (
  showAlertModal?: (title: string, message: string) => void
): Promise<boolean> => {
  try {
    console.log('[Beko Token] Cihaz başlatılıyor...');
    
    const statusResult = await callTokenAPI('token-status', {}, 'GET', showAlertModal);
    
    if (!statusResult.success) {
      const initResult = await callTokenAPI(
        'token-initialize',
        { appName: 'GTS YAZILIM' },
        'POST',
        showAlertModal
      );
      
      if (!initResult.success) {
        console.error('[Beko Token] Başlatma başarısız');
        return false;
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    console.log('[Beko Token] Fiscal Info alınıyor...');
    const fiscalInfoResult = await callTokenAPI('token-fiscal-info', {}, 'POST', showAlertModal);
    
    if (!fiscalInfoResult.success || !fiscalInfoResult.data.rawJson) {
      console.error('[Beko Token] Fiscal Info alınamadı');
      return false;
    }
    
    fiscalInfoCache = JSON.parse(fiscalInfoResult.data.rawJson);
    
    const sectionsWithLimits = (fiscalInfoCache?.sections || []).filter((s: TokenSection) => s.limit && s.limit > 0);
    const receiptLimitTL = fiscalInfoCache?.receiptLimit ? (fiscalInfoCache.receiptLimit / 100) : null;
    
    console.log('[Beko Token] Fiscal Info cache\'lendi:', {
      sectionCount: fiscalInfoCache?.sections?.length || 0,
      pluCount: fiscalInfoCache?.plus?.length || 0,
      receiptLimit: receiptLimitTL ? `${receiptLimitTL} TL` : 'Yok',
      sectionsWithLimits: sectionsWithLimits.map((s: TokenSection) => `${s.name}: ${(s.limit / 100)} TL`)
    });
    
    isInitialized = true;
    return true;
    
  } catch (error: any) {
    console.error('[Beko Token] Başlatma hatası:', error);
    return false;
  }
};

const findSectionForProduct = (
  product: FiscalProductItem,
  showAlertModal?: (title: string, message: string) => void
): TokenSection | null => {
  if (!fiscalInfoCache || !fiscalInfoCache.sections || fiscalInfoCache.sections.length === 0) {
    console.error('[Beko Token] Fiscal Info cache boş veya geçersiz');
    return null;
  }
  
  const targetTaxPercent = product.vatRate * 100;
  
  let section = fiscalInfoCache.sections.find(s => s.taxPercent === targetTaxPercent);
  
  if (!section && fiscalInfoCache.sections.length > 0) {
    console.warn(`[Beko Token] Ürün "${product.name}" için %${product.vatRate} KDV oranı bulunamadı, en yakın kısım kullanılıyor`);
    
    const sortedSections = [...fiscalInfoCache.sections].sort((a, b) => {
      const diffA = Math.abs(a.taxPercent - targetTaxPercent);
      const diffB = Math.abs(b.taxPercent - targetTaxPercent);
      return diffA - diffB;
    });
    section = sortedSections[0];
  }
  
  return section || null;
};

/**
 * Kısım limitlerini ve fiş limitini kontrol eder.
 * Hata varsa Türkçe açıklama döner, yoksa null döner.
 */
const validateSectionAndReceiptLimits = (
  products: FiscalProductItem[]
): string | null => {
  if (!fiscalInfoCache) return null;

  // Fiş limiti kontrolü (receiptLimit kuruş cinsinden)
  if (fiscalInfoCache.receiptLimit && fiscalInfoCache.receiptLimit > 0) {
    const totalKurus = products.reduce((sum, p) => sum + Math.round(p.price * 100) * p.quantity, 0);
    if (totalKurus > fiscalInfoCache.receiptLimit) {
      const limitTL = (fiscalInfoCache.receiptLimit / 100).toLocaleString('tr-TR');
      const totalTL = (totalKurus / 100).toLocaleString('tr-TR');
      return `Sepet toplamı (${totalTL} TL) cihazın fiş limitini (${limitTL} TL) aşmaktadır.\n\nLütfen sepet bölme özelliğini kullanınız.`;
    }
  }

  // Kısım limiti kontrolü (her kısım için toplam tutarı hesapla)
  const sectionTotals: { [sectionNo: number]: { total: number; sectionName: string; limit: number } } = {};
  
  for (const product of products) {
    const section = findSectionForProduct(product);
    if (!section) continue;
    
    if (section.limit && section.limit > 0) {
      if (!sectionTotals[section.sectionNo]) {
        sectionTotals[section.sectionNo] = { total: 0, sectionName: section.name, limit: section.limit };
      }
      sectionTotals[section.sectionNo].total += Math.round(product.price * 100) * product.quantity;
    }
  }

  for (const sectionNo of Object.keys(sectionTotals)) {
    const info = sectionTotals[Number(sectionNo)];
    if (info.total > info.limit) {
      const limitTL = (info.limit / 100).toLocaleString('tr-TR');
      const totalTL = (info.total / 100).toLocaleString('tr-TR');
      return `"${info.sectionName}" kısmındaki ürünlerin toplamı (${totalTL} TL) bu kısmın limitini (${limitTL} TL) aşmaktadır.\n\nBu kısımda daha fazla ürün satışı yapılamaz.`;
    }
  }

  return null;
};

/**
 * Ürünlerden Token basket item listesi oluşturur
 */
const buildBasketItems = (
  products: FiscalProductItem[],
  showAlertModal?: (title: string, message: string) => void
) => {
  // Kısım ve fiş limiti kontrolü
  const limitError = validateSectionAndReceiptLimits(products);
  if (limitError) {
    throw new Error(limitError);
  }

  return products.map(product => {
    const section = findSectionForProduct(product, showAlertModal);
    
    if (!section) {
      throw new Error(`Ürün "${product.name}" için uygun kısım bulunamadı`);
    }
    
    return {
      barcode: product.barcode || '',
      name: product.name,
      pluNo: 0,
      price: Math.round(product.price * 100),
      sectionNo: section.sectionNo,
      taxPercent: section.taxPercent,
      type: 0,
      unit: 'Adet',
      vatID: 0,
      limit: 0,
      quantity: Math.round(product.quantity * 1000),
      paymentType: 0
    };
  });
};

/**
 * Cihazın başlatılmış olmasını garanti eder
 */
const ensureInitialized = async (
  showAlertModal?: (title: string, message: string) => void
): Promise<boolean> => {
  if (!isInitialized || !fiscalInfoCache) {
    return await initializeBekoTokenDevice(showAlertModal);
  }
  return true;
};

/**
 * Cihaz türünü (X30TR=0, 300TR=1) döndürür
 */
const getDeviceIndex = async (): Promise<number> => {
  try {
    const result = await callTokenAPI('token-status', {}, 'GET');
    if (result.success) {
      return result.data?.data?.deviceIndex ?? -1;
    }
    return -1;
  } catch {
    return -1;
  }
};

// Beko X30TR / 300TR entegrasyonu
export class BekoTokenDevice implements IFiscalDevice {
  /**
   * Nakit ödeme
   */
  async processCashPayment(
    products: FiscalProductItem[],
    totalAmount: number,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Beko Token] Nakit ödeme işlemi başlıyor...', { totalAmount, productCount: products.length });
      
      if (!(await ensureInitialized(showAlertModal))) {
        return { success: false, errorMessage: 'Beko cihazı başlatılamadı' };
      }
      
      const basketItems = buildBasketItems(products, showAlertModal);
      
      const basket = {
        basketID: generateUUID(),
        createInvoice: false,
        documentType: 0,
        isVoid: false,
        items: basketItems,
        paymentItems: [{
          amount: Math.round(totalAmount * 100),
          description: 'NAKIT',
          type: 1
        }]
      };
      
      console.log('[Beko Token] Sepet oluşturuldu:', basket);
      
      const result = await callTokenAPI('token-send-basket', { basket, targetDeviceIndex, maxWaitMs: 90000 }, 'POST', showAlertModal);
      
      if (!result.success) {
        return { success: false, errorMessage: result.data?.message || 'Sepet gönderilemedi' };
      }
      
      const { actualPaymentItems, paymentTypeChanged } = parseReceiptPaymentItems(
        result.data?.data?.receiptData,
        [1]
      );
      
      console.log('[Beko Token] Nakit ödeme başarılı', paymentTypeChanged ? '(ödeme türü cihazda değiştirildi!)' : '');
      
      return {
        success: true,
        documentId: basket.basketID,
        fiscalData: {
          BasketID: basket.basketID,
          DeviceType: result.data?.data?.deviceType || 'Beko',
          ActualPaymentItems: actualPaymentItems.length > 0 ? actualPaymentItems : undefined,
          PaymentTypeChanged: paymentTypeChanged || undefined,
        }
      };
      
    } catch (error: any) {
      console.error('[Beko Token] Nakit ödeme hatası:', error);
      return { success: false, errorMessage: error.message || 'İşlem başarısız' };
    }
  }

  /**
   * POS ödeme
   */
  async processPosPayment(
    products: FiscalProductItem[],
    totalAmount: number,
    showAlertModal?: (title: string, message: string) => void,
    cashAmount?: number,
    companyId?: string
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Beko Token] POS ödeme işlemi başlıyor...', { 
        totalAmount, cashAmount, productCount: products.length 
      });
      
      if (!(await ensureInitialized(showAlertModal))) {
        return { success: false, errorMessage: 'Beko cihazı başlatılamadı' };
      }
      
      const basketItems = buildBasketItems(products, showAlertModal);
      
      const paymentItems = [];
      
      if (cashAmount && cashAmount > 0) {
        paymentItems.push({
          amount: Math.round((totalAmount - cashAmount) * 100),
          description: 'KREDİ KARTI',
          type: 3
        });
        paymentItems.push({
          amount: Math.round(cashAmount * 100),
          description: 'NAKIT',
          type: 1
        });
      } else {
        paymentItems.push({
          amount: Math.round(totalAmount * 100),
          description: 'KREDİ KARTI',
          type: 3
        });
      }
      
      const basket = {
        basketID: generateUUID(),
        createInvoice: false,
        documentType: 0,
        isVoid: false,
        items: basketItems,
        paymentItems: paymentItems
      };
      
      console.log('[Beko Token] POS sepeti oluşturuldu:', basket);
      
      const sentPaymentTypes = paymentItems.map(p => p.type);
      const result = await callTokenAPI('token-send-basket', { basket, targetDeviceIndex, maxWaitMs: 90000 }, 'POST', showAlertModal);
      
      if (!result.success) {
        return { success: false, errorMessage: result.data?.message || 'Sepet gönderilemedi' };
      }
      
      const { actualPaymentItems, paymentTypeChanged } = parseReceiptPaymentItems(
        result.data?.data?.receiptData,
        sentPaymentTypes
      );
      
      let resolvedPaymentType = cashAmount ? 'KARMA' : 'POS';
      if (paymentTypeChanged && actualPaymentItems.length > 0) {
        const allCash = actualPaymentItems.every(p => p.type === 1);
        const allPos = actualPaymentItems.every(p => p.type === 3);
        if (allCash) resolvedPaymentType = 'NAKIT';
        else if (allPos) resolvedPaymentType = 'POS';
        else resolvedPaymentType = 'KARMA';
      }
      
      console.log('[Beko Token] POS ödeme başarılı', paymentTypeChanged ? `(ödeme türü değişti: ${resolvedPaymentType})` : '');
      
      return {
        success: true,
        documentId: basket.basketID,
        fiscalData: {
          BasketID: basket.basketID,
          DeviceType: result.data?.data?.deviceType || 'Beko',
          PaymentType: resolvedPaymentType,
          ActualPaymentItems: actualPaymentItems.length > 0 ? actualPaymentItems : undefined,
          PaymentTypeChanged: paymentTypeChanged || undefined,
        }
      };
      
    } catch (error: any) {
      console.error('[Beko Token] POS ödeme hatası:', error);
      return { success: false, errorMessage: error.message || 'İşlem başarısız' };
    }
  }

  /**
   * Çoklu ödeme işlemi (Birden fazla POS + NAKİT)
   * X30TR: sendBasket ile tüm ödemeler tek seferde
   * 300TR: sendBasket (ürünler) + sendPayment (her ödeme ayrı ayrı)
   */
  async processMultiplePayments(
    products: FiscalProductItem[],
    posPayments: MultiplePosPayment[],
    cashAmount?: number,
    showAlertModal?: (title: string, message: string) => void,
    stepCallback?: PaymentStepCallback
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Beko Token] Çoklu ödeme işlemi başlıyor...', {
        productCount: products.length,
        posPaymentCount: posPayments.length,
        cashAmount
      });

      if (!(await ensureInitialized(showAlertModal))) {
        return { success: false, errorMessage: 'Beko cihazı başlatılamadı' };
      }

      const basketItems = buildBasketItems(products, showAlertModal);
      const deviceIndex = await getDeviceIndex();

      // Ödeme listesi oluştur
      const paymentItems: Array<{ amount: number; description: string; type: number }> = [];

      posPayments.forEach((pp, idx) => {
        paymentItems.push({
          amount: Math.round(pp.amount * 100),
          description: `KREDİ KARTI ${idx + 1}`,
          type: 3
        });
      });

      if (cashAmount && cashAmount > 0) {
        paymentItems.push({
          amount: Math.round(cashAmount * 100),
          description: 'NAKIT',
          type: 1
        });
      }

      if (deviceIndex === 1) {
        // 300TR: Sepeti ödemesiz gönder, sonra her ödemeyi ayrı ayrı gönder
        const basket = {
          basketID: generateUUID(),
          createInvoice: false,
          documentType: 0,
          isVoid: false,
          items: basketItems,
          paymentItems: [] as any[]
        };

        console.log('[Beko Token] 300TR: Sepet gönderiliyor (ödemesiz)...');
        const basketResult = await callTokenAPI('token-send-basket', { basket, targetDeviceIndex, maxWaitMs: 120000 }, 'POST', showAlertModal);

        if (!basketResult.success) {
          return { success: false, errorMessage: basketResult.data?.message || 'Sepet gönderilemedi' };
        }

        console.log('[Beko Token] 300TR: Sepet kabul edildi, ödemeler gönderiliyor...');

        // Her ödemeyi ayrı ayrı gönder
        for (let i = 0; i < paymentItems.length; i++) {
          const payment = paymentItems[i];
          const stepType = payment.type === 1 ? 'cash' as const : 'pos' as const;
          const amountTL = payment.amount / 100;
          const paymentMaxWaitMs = payment.type === 3 ? 90000 : 30000;

          stepCallback?.onStepStart?.(i, stepType, amountTL, payment.description);

          console.log(`[Beko Token] 300TR: Ödeme ${i + 1}/${paymentItems.length} gönderiliyor...`, payment);
          const paymentResult = await callTokenAPI('token-send-payment', { payment, maxWaitMs: paymentMaxWaitMs }, 'POST', showAlertModal);

          if (!paymentResult.success) {
            stepCallback?.onStepError?.(i, stepType, amountTL, paymentResult.data?.message || 'Ödeme gönderilemedi');
            return { success: false, errorMessage: paymentResult.data?.message || `Ödeme ${i + 1} gönderilemedi` };
          }

          stepCallback?.onStepSuccess?.(i, stepType, amountTL);
        }

        console.log('[Beko Token] 300TR: Tüm ödemeler başarılı');
        return {
          success: true,
          documentId: basket.basketID,
          fiscalData: {
            BasketID: basket.basketID,
            DeviceType: '300TR',
            PaymentType: 'MULTIPLE'
          }
        };

      } else {
        // X30TR: Tüm ödemeleri sepet ile birlikte tek seferde gönder
        const basket = {
          basketID: generateUUID(),
          createInvoice: false,
          documentType: 0,
          isVoid: false,
          items: basketItems,
          paymentItems: paymentItems
        };

        console.log('[Beko Token] X30TR: Sepet + ödemeler tek seferde gönderiliyor...');

        // Tüm adımları başlat bildirimini gönder
        paymentItems.forEach((p, idx) => {
          const stepType = p.type === 1 ? 'cash' as const : 'pos' as const;
          stepCallback?.onStepStart?.(idx, stepType, p.amount / 100, p.description);
        });

        const sentPaymentTypes = paymentItems.map(p => p.type);
        const result = await callTokenAPI('token-send-basket', { basket, targetDeviceIndex, maxWaitMs: 120000 }, 'POST', showAlertModal);

        if (!result.success) {
          return { success: false, errorMessage: result.data?.message || 'Sepet gönderilemedi' };
        }

        const { actualPaymentItems, paymentTypeChanged } = parseReceiptPaymentItems(
          result.data?.data?.receiptData,
          sentPaymentTypes
        );

        // Tüm adımları başarılı bildirimini gönder
        paymentItems.forEach((p, idx) => {
          const stepType = p.type === 1 ? 'cash' as const : 'pos' as const;
          stepCallback?.onStepSuccess?.(idx, stepType, p.amount / 100);
        });

        let resolvedPaymentType = 'MULTIPLE';
        if (paymentTypeChanged && actualPaymentItems.length > 0) {
          const allCash = actualPaymentItems.every(p => p.type === 1);
          const allPos = actualPaymentItems.every(p => p.type === 3);
          if (allCash) resolvedPaymentType = 'NAKIT';
          else if (allPos) resolvedPaymentType = 'POS';
        }

        console.log('[Beko Token] X30TR: Çoklu ödeme başarılı', paymentTypeChanged ? `(ödeme türü değişti: ${resolvedPaymentType})` : '');
        return {
          success: true,
          documentId: basket.basketID,
          fiscalData: {
            BasketID: basket.basketID,
            DeviceType: 'X30TR',
            PaymentType: resolvedPaymentType,
            ActualPaymentItems: actualPaymentItems.length > 0 ? actualPaymentItems : undefined,
            PaymentTypeChanged: paymentTypeChanged || undefined,
          }
        };
      }

    } catch (error: any) {
      console.error('[Beko Token] Çoklu ödeme hatası:', error);
      return { success: false, errorMessage: error.message || 'İşlem başarısız' };
    }
  }

  /**
   * Açık belgeyi iptal et (sadece 300TR)
   * X30TR için no-op döner çünkü X30TR'de açık belge kavramı yoktur.
   */
  async cancelDocument(
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      const deviceIndex = await getDeviceIndex();

      if (deviceIndex !== 1) {
        console.log('[Beko Token] X30TR: cancelDocument desteklenmiyor (no-op)');
        return { success: true };
      }

      console.log('[Beko Token] 300TR: Fiş iptal ediliyor...');
      const result = await callTokenAPI('token-cancel-receipt', {}, 'POST', showAlertModal);

      if (!result.success) {
        return { success: false, errorMessage: result.data?.message || 'Fiş iptal edilemedi' };
      }

      console.log('[Beko Token] 300TR: Fiş iptal edildi');
      return { success: true };

    } catch (error: any) {
      console.error('[Beko Token] Fiş iptal hatası:', error);
      return { success: false, errorMessage: error.message || 'İşlem başarısız' };
    }
  }

  /**
   * Yazarkasa bağlantı kontrolü
   */
  async checkConnection(): Promise<boolean> {
    try {
      const result = await callTokenAPI('token-status', {}, 'GET');
      const connected = result.success && result.data?.data?.isConnected === true;
      
      if (connected && targetDeviceIndex >= 0) {
        const actualIndex = result.data?.data?.deviceIndex ?? -1;
        if (actualIndex >= 0 && actualIndex !== targetDeviceIndex) {
          const expectedName = targetDeviceIndex === 0 ? 'Beko X30TR' : 'Beko 300TR';
          const actualName = result.data?.data?.deviceType || (actualIndex === 0 ? 'Beko X30TR' : 'Beko 300TR');
          console.warn(`[Beko Token] CIHAZ UYUMSUZLUGU: Ayarlarda ${expectedName} secili fakat ${actualName} bagli!`);
        }
      }
      
      return connected;
    } catch (error) {
      console.error('[Beko Token] Bağlantı kontrolü hatası:', error);
      return false;
    }
  }

  /**
   * Yazarkasa bilgilerini al
   */
  async getDeviceInfo(): Promise<any> {
    try {
      const result = await callTokenAPI('token-status', {}, 'GET');
      if (result.success) {
        return {
          isConnected: result.data?.data?.isConnected || false,
          fiscalId: result.data?.data?.fiscalId || '',
          deviceType: result.data?.data?.deviceType || 'Unknown',
          deviceIndex: result.data?.data?.deviceIndex || -1
        };
      }
      return null;
    } catch (error) {
      console.error('[Beko Token] Cihaz bilgisi alınamadı:', error);
      return null;
    }
  }

  /**
   * Cihazın fiş limitini ve kısım limitlerini döner.
   * Cihaz bağlı değilse veya fiscal info alınamadıysa null döner.
   */
  getDeviceLimits(): { receiptLimit: number | null; sectionLimits: { sectionNo: number; name: string; limit: number; taxPercent: number }[] } | null {
    if (!fiscalInfoCache) return null;
    
    const sectionLimits = (fiscalInfoCache.sections || [])
      .filter(s => s.limit && s.limit > 0)
      .map(s => ({ sectionNo: s.sectionNo, name: s.name, limit: s.limit, taxPercent: s.taxPercent }));

    return {
      receiptLimit: fiscalInfoCache.receiptLimit || null,
      sectionLimits
    };
  }
}

// Singleton instance
export const bekoTokenDevice: IFiscalDevice & { getDeviceLimits: () => { receiptLimit: number | null; sectionLimits: { sectionNo: number; name: string; limit: number; taxPercent: number }[] } | null } = new BekoTokenDevice();
